import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { getAnnotationConfigs, AnnotationConfig } from '@/config/annotationRegistry';
import { supabase } from '@/integrations/supabase/client';
import { useBrandSelection } from '@/hooks/useBrandSelection';

interface AnnotationModeContextType {
  isAnnotationModeEnabled: boolean;
  toggleAnnotationMode: () => void;
  enableAnnotationMode: () => void;
  disableAnnotationMode: () => void;
  currentConfigs: AnnotationConfig[];
  isRouteRegistered: boolean;
  setAnnotationModeEnabled: (enabled: boolean) => void;
}

const AnnotationModeContext = createContext<AnnotationModeContextType | undefined>(undefined);

interface AnnotationModeProviderProps {
  children: ReactNode;
}

export function AnnotationModeProvider({ children }: AnnotationModeProviderProps) {
  const [isAnnotationModeEnabled, setIsAnnotationModeEnabled] = useState(false);
  const location = useLocation();
  const { currentBrandId } = useBrandSelection();
  
  // Get annotation configs for current route
  const currentConfigs = getAnnotationConfigs(location.pathname);
  const isRouteRegistered = currentConfigs.length > 0;
  
  // Load annotation mode setting from database on mount
  useEffect(() => {
    const loadAnnotationModeSetting = async () => {
      if (!currentBrandId) return;
      
      try {
        const { data, error } = await (supabase
          .from('os_annotation_settings' as any)
          .select('annotation_mode_enabled')
          .eq('brand_id', currentBrandId)
          .maybeSingle()) as any;
        
        if (!error && data?.annotation_mode_enabled !== undefined) {
          setIsAnnotationModeEnabled(data.annotation_mode_enabled);
        }
      } catch (err) {
        console.error('Failed to load annotation mode setting:', err);
      }
    };
    
    loadAnnotationModeSetting();
  }, [currentBrandId]);
  
  // Note: We don't auto-disable on unregistered routes anymore
  // The overlay simply won't show anything if there are no configs

  const toggleAnnotationMode = useCallback(() => {
    setIsAnnotationModeEnabled(prev => !prev);
  }, []);

  const enableAnnotationMode = useCallback(() => {
    setIsAnnotationModeEnabled(true);
  }, []);

  const disableAnnotationMode = useCallback(() => {
    setIsAnnotationModeEnabled(false);
  }, []);
  
  const setAnnotationModeEnabled = useCallback((enabled: boolean) => {
    setIsAnnotationModeEnabled(enabled);
  }, []);

  return (
    <AnnotationModeContext.Provider
      value={{
        isAnnotationModeEnabled,
        toggleAnnotationMode,
        enableAnnotationMode,
        disableAnnotationMode,
        currentConfigs,
        isRouteRegistered,
        setAnnotationModeEnabled,
      }}
    >
      {children}
    </AnnotationModeContext.Provider>
  );
}

export function useAnnotationMode() {
  const context = useContext(AnnotationModeContext);
  if (context === undefined) {
    throw new Error('useAnnotationMode must be used within an AnnotationModeProvider');
  }
  return context;
}
